import React from 'react';
import { fetchDealCards } from './api';
import type { DealCard } from './api';
import { ChevronIcon, SearchIcon } from './icons';
import { Empty, ErrorBox, LoadingBlock, Pill, fmtDate, fmtNum, stageTone, useLoad } from './ui';

export const DealRow: React.FC<{ deal: DealCard; onOpen: () => void }> = ({ deal, onOpen }) => {
  const c = deal.counts;
  const done = c.total ? (c.slaughtered / c.total) * 100 : 0;
  const out = c.total ? (c.deceased / c.total) * 100 : 0;
  const meta = [deal.species, deal.programme].filter(Boolean).join(' · ');
  return (
    <button type="button" className="pf-deal" onClick={onOpen} aria-label={`Open deal ${deal.deal_no}`}>
      <span className="pf-deal-row">
        <span>
          <span className="pf-deal-no">{deal.deal_no}</span>
          {meta && <span className="pf-deal-meta" style={{ display: 'block' }}>{meta}</span>}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Pill tone={stageTone(deal.stage)}>{deal.stage}</Pill>
          <span className="pf-chev"><ChevronIcon size={20} /></span>
        </span>
      </span>
      {c.total > 0 && (
        <span className="pf-track" aria-hidden="true">
          <i className="is-done" style={{ width: `${done}%` }} />
          <i className="is-out" style={{ width: `${out}%` }} />
        </span>
      )}
      <span className="pf-deal-foot">
        <span>
          {c.total > 0
            ? <><b className="pf-num">{fmtNum(c.total)}</b> head{c.alive > 0 ? ` · ${fmtNum(c.alive)} still on the go` : c.slaughtered ? ` · ${fmtNum(c.slaughtered)} slaughtered` : ''}</>
            : 'Animals not loaded yet'}
        </span>
        {deal.started && <span className="pf-small">{fmtDate(deal.started)}</span>}
      </span>
    </button>
  );
};

type Filter = 'all' | 'open' | 'complete';

export const Deals: React.FC<{ onOpenDeal: (id: string) => void }> = ({ onOpenDeal }) => {
  const { data, error, loading, retry } = useLoad<DealCard[]>(fetchDealCards);
  const [filter, setFilter] = React.useState<Filter>('all');
  const [q, setQ] = React.useState('');

  const rows = (data || []).filter(d => {
    if (filter === 'open' && d.stage === 'Complete') return false;
    if (filter === 'complete' && d.stage !== 'Complete') return false;
    const needle = q.trim().toLowerCase();
    return !needle || `${d.deal_no} ${d.reference} ${d.species || ''} ${d.programme || ''}`.toLowerCase().includes(needle);
  });

  return (
    <div className="pf-screen">
      <header className="pf-top">
        <div className="pf-wrap">
          <h1 className="pf-h1">Your deals</h1>
          <p className="pf-sub" style={{ marginTop: 4 }}>
            {data ? `${fmtNum(data.length)} in total` : ' '}
          </p>
        </div>
      </header>
      <main className="pf-wrap" style={{ paddingTop: 16 }}>
        <div className="pf-stack">
          <label className="pf-field">
            <span className="pf-sr">Search deals</span>
            <SearchIcon size={20} />
            <input type="search" inputMode="search" placeholder="Search by deal number" value={q} onChange={e => setQ(e.target.value)} />
          </label>
          <div className="pf-chips" role="group" aria-label="Filter deals">
            {(['all', 'open', 'complete'] as Filter[]).map(f => (
              <button key={f} type="button" className="pf-chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'open' ? 'In progress' : 'Complete'}
              </button>
            ))}
          </div>
          {loading ? <LoadingBlock rows={3} /> : error ? <ErrorBox message={error} onRetry={retry} /> : rows.length === 0 ? (
            <Empty title={data && data.length ? 'No deals match' : 'No deals yet'}>
              {data && data.length ? 'Try a different filter or search.' : 'When AVCS sets up a deal for you it will show here.'}
            </Empty>
          ) : (
            <ul className="pf-list" aria-live="polite">
              {rows.map(d => <li key={d.id}><DealRow deal={d} onOpen={() => onOpenDeal(d.id)} /></li>)}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};
